import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDocs,
  where,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsUsEast1 } from '../../../utils/init-firebase';
import {
  MeetingSummary,
  ActiveMeeting,
  L10Note,
  Issue,
  Rock,
  ActionItem,
  MeetingIssue,
  UnifiedMeeting,
  COLLECTION_NAMES,
  User,
  Department,
} from '../types';
import { useAuth } from '../../../contexts/AuthContext';
import jsPDF from 'jspdf';
/**
 * Hook for Meeting Summaries data with Firestore
 * Manages meeting summary generation, storage, and retrieval
 */
export function useMeetingSummaries(userId: string | undefined) {
  const [summaries, setSummaries] = useState<MeetingSummary[]>([]);
  const [unifiedMeetings, setUnifiedMeetings] = useState<UnifiedMeeting[]>([]);
  const { eosUsers = [] } = useAuth();
  const [loading, setLoading] = useState(true);

  // Load unified meetings (combines l10_notes and meeting_summaries)
  const loadUnifiedMeetings = useCallback(async (showLoading: boolean = true) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }

      const [meetingsResult, summariesResult] = await Promise.all([
        getDocs(
          query(
            collection(db, COLLECTION_NAMES.L10_NOTES),
            orderBy('meetingTimestamp', 'desc')
          )
        ),
        getDocs(
          query(
            collection(db, COLLECTION_NAMES.MEETING_SUMMARIES),
            orderBy('meetingTimestamp', 'desc')
          )
        ),
      ]);

      // Helper to convert to Timestamp
      const toTimestamp = (value: any): Timestamp => {
        if (value instanceof Timestamp) {
          return value;
        }
        if (value && typeof value === 'object' && 'seconds' in value) {
          // Firestore Timestamp object (not instance of Timestamp class)
          return Timestamp.fromMillis(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
        }
        if (value instanceof Date) {
          return Timestamp.fromDate(value);
        }
        if (typeof value === 'string') {
          return Timestamp.fromDate(new Date(value));
        }
        return Timestamp.now();
      };

      // Helper to convert to Date
      const toDate = (value: any): Date => {
        if (value instanceof Date) {
          return value;
        }
        if (value instanceof Timestamp) {
          return value.toDate();
        }
        if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
          return value.toDate();
        }
        if (value && typeof value === 'object' && 'seconds' in value) {
          return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
        }
        if (typeof value === 'string') {
          return new Date(value);
        }
        return new Date();
      };

      // Get unique meetings from l10_notes
      const meetingsMap = new Map<string, { meetingTimestamp: Timestamp; meetingDate: Date | string }>();
      meetingsResult.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.meetingTimestamp;
        if (timestamp) {
          const meetingTimestamp = toTimestamp(timestamp);
          const timestampKey = meetingTimestamp.toMillis().toString();
          if (!meetingsMap.has(timestampKey)) {
            meetingsMap.set(timestampKey, {
              meetingTimestamp,
              meetingDate: data.meetingDate ? toDate(data.meetingDate) : meetingTimestamp.toDate(),
            });
          }
        }
      });

      // Map summaries by meeting timestamp
      const summariesMap = new Map<string, MeetingSummary>();
      summariesResult.docs.forEach((doc) => {
        const data = doc.data();
        const meetingTimestamp = toTimestamp(data.meetingTimestamp);
        const meetingDate = data.meetingDate ? toDate(data.meetingDate) : meetingTimestamp.toDate();
        const createdAt = data.createdAt ? toTimestamp(data.createdAt) : Timestamp.now();
        
        const summary: MeetingSummary = {
          id: doc.id,
          ...data,
          meetingTimestamp,
          meetingDate,
          summary: data.summary || '',
          createdAt,
        };
        const timestampKey = meetingTimestamp.toMillis().toString();
        summariesMap.set(timestampKey, summary);
        
        // Also add summaries to meetingsMap if they don't have a corresponding l10_note
        if (!meetingsMap.has(timestampKey)) {
          meetingsMap.set(timestampKey, {
            meetingTimestamp,
            meetingDate,
          });
        }
      });

      // Create unified meetings from all meetings (both from l10_notes and summaries)
      const unified: UnifiedMeeting[] = Array.from(meetingsMap.values()).map((meeting) => {
        const timestampKey = meeting.meetingTimestamp instanceof Timestamp
          ? meeting.meetingTimestamp.toMillis().toString()
          : String(meeting.meetingTimestamp);
        return {
          meetingTimestamp: meeting.meetingTimestamp,
          meetingDate: meeting.meetingDate,
          summary: summariesMap.get(timestampKey),
          hasSummary: summariesMap.has(timestampKey),
        };
      });

      setUnifiedMeetings(unified);
      setSummaries(Array.from(summariesMap.values()));
    } catch (error) {
      console.error('Error loading unified meetings:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [userId]);

  // Real-time listener for meeting summaries - also updates unified meetings
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const summariesQuery = query(
      collection(db, COLLECTION_NAMES.MEETING_SUMMARIES),
      orderBy('meetingTimestamp', 'desc')
    );

    const unsubscribeSummaries = onSnapshot(
      summariesQuery,
      async (snapshot) => {
        const summariesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            meetingTimestamp: data.meetingTimestamp || Timestamp.now(),
            meetingDate: data.meetingDate || data.meetingTimestamp?.toDate?.() || new Date(),
            createdAt: data.createdAt || Timestamp.now(),
          } as MeetingSummary;
        });
        setSummaries(summariesData);
        
        // Also reload unified meetings when summaries change
        // Don't show loading state for real-time updates
        await loadUnifiedMeetings(false);
      },
      (error) => {
        console.error('Error loading meeting summaries:', error);
      }
    );

    // Real-time listener for l10_notes - updates unified meetings when new meetings are completed
    const notesQuery = query(
      collection(db, COLLECTION_NAMES.L10_NOTES),
      orderBy('meetingTimestamp', 'desc')
    );

    // Debounce timer for notes listener to prevent too many rapid reloads
    let notesDebounceTimer: NodeJS.Timeout | null = null;
    let lastSnapshotSize = 0;
    let isInitialLoad = true;

    const unsubscribeNotes = onSnapshot(
      notesQuery,
      async (snapshot) => {
        // On initial load, just set the size and skip reload (loadUnifiedMeetings already called on mount)
        if (isInitialLoad) {
          lastSnapshotSize = snapshot.docs.length;
          isInitialLoad = false;
          return;
        }

        // Check if this is a meaningful change (new documents added or removed)
        const currentSize = snapshot.docs.length;
        const changes = snapshot.docChanges();
        const hasChanges = changes.length > 0;
        
        // If no changes detected, skip reload
        if (!hasChanges && currentSize === lastSnapshotSize) {
          return;
        }
        
        // Log changes for debugging
        if (hasChanges) {
          console.log('[MeetingSummaries] Notes changed:', {
            added: changes.filter(c => c.type === 'added').length,
            modified: changes.filter(c => c.type === 'modified').length,
            removed: changes.filter(c => c.type === 'removed').length,
            totalDocs: currentSize,
          });
        }
        
        lastSnapshotSize = currentSize;

        // Clear existing debounce timer
        if (notesDebounceTimer) {
          clearTimeout(notesDebounceTimer);
        }

        // Debounce the reload to handle batch writes (like when saving multiple notes)
        // Reduced debounce time to 300ms for faster updates
        // This prevents race conditions where the listener fires before all notes are committed
        notesDebounceTimer = setTimeout(async () => {
          try {
            // Reload unified meetings when notes change (new meetings completed)
            // Don't show loading state for real-time updates to avoid hiding existing meetings
            await loadUnifiedMeetings(false);
          } catch (error) {
            console.error('Error reloading unified meetings from notes listener:', error);
            // Fallback to full reload with loading state if there's an error
            await loadUnifiedMeetings(true);
          }
        }, 300); // 300ms debounce (reduced from 500ms)
      },
      (error) => {
        console.error('Error loading l10 notes:', error);
        // On error, try a full reload
        loadUnifiedMeetings(true).catch((err) => {
          console.error('Error in fallback reload:', err);
        });
      }
    );

    return () => {
      unsubscribeSummaries();
      unsubscribeNotes();
      // Clear debounce timer on cleanup
      if (notesDebounceTimer) {
        clearTimeout(notesDebounceTimer);
      }
    };
  }, [userId, loadUnifiedMeetings]);

  // Load unified meetings on mount
  useEffect(() => {
    loadUnifiedMeetings();
  }, [loadUnifiedMeetings]);

  // Fetch meeting-specific data (rocks, issues, todos)
  const fetchMeetingData = useCallback(async (meetingTimestamp: Timestamp) => {
    try {
      const timestampStr = meetingTimestamp instanceof Timestamp
        ? meetingTimestamp.toDate().toISOString()
        : String(meetingTimestamp);

      // Fetch meeting issues
      const meetingIssuesQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_ISSUES),
        where('meetingTimestamp', '==', meetingTimestamp)
      );
      const meetingIssuesSnapshot = await getDocs(meetingIssuesQuery);
      const meetingIssues = meetingIssuesSnapshot.docs.map((doc) => doc.data() as MeetingIssue);

      // Fetch full issue details
      const issuesWithDetails = await Promise.all(
        meetingIssues.map(async (meetingIssue) => {
          try {
            const issueDoc = await getDoc(doc(db, COLLECTION_NAMES.ISSUES, meetingIssue.issueId));
            if (issueDoc.exists()) {
              return {
                ...issueDoc.data(),
                id: issueDoc.id,
              } as Issue;
            }
            return null;
          } catch (error) {
            console.error('Error loading issue:', error);
            return null;
          }
        })
      );
      const issues = issuesWithDetails.filter((i): i is Issue => i !== null);

      // Fetch rocks (all rocks, we'll filter by achievement status)
      const rocksQuery = query(collection(db, COLLECTION_NAMES.ROCKS));
      const rocksSnapshot = await getDocs(rocksQuery);
      const allRocks = rocksSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Rock[];

      // Fetch action items created in this meeting
      const actionItemsQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('createdInMeetingId', '==', timestampStr)
      );
      const actionItemsSnapshot = await getDocs(actionItemsQuery);
      const actionItems = actionItemsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ActionItem[];

      return {
        issues,
        rocks: allRocks,
        actionItems,
      };
    } catch (error) {
      console.error('Error fetching meeting data:', error);
      return {
        issues: [],
        rocks: [],
        actionItems: [],
      };
    }
  }, []);

  // Generate summary for a meeting
  const generateSummary = async (meetingTimestamp: Timestamp | string) => {
    try {
      // Convert to Timestamp if needed
      const timestamp = typeof meetingTimestamp === 'string' 
        ? Timestamp.fromDate(new Date(meetingTimestamp))
        : meetingTimestamp;
      
      // Check if summary already exists or is in progress
      const existingQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_SUMMARIES),
        where('meetingTimestamp', '==', timestamp)
      );
      const existingSnapshot = await getDocs(existingQuery);
      if (!existingSnapshot.empty) {
        const existingDoc = existingSnapshot.docs[0];
        const existingData = existingDoc.data();
        const status = existingData.status || 'completed';
        
        if (status === 'pending' || status === 'generating') {
          throw new Error('Summary generation already in progress for this meeting');
        }
        
        if (status === 'completed') {
          throw new Error('Summary already exists for this meeting');
        }
      }

      // Fetch meeting data
      const [notesSnapshot, activeMeetingsSnapshot, meetingData] = await Promise.all([
        getDocs(
          query(
          collection(db, COLLECTION_NAMES.L10_NOTES),
          where('meetingTimestamp', '==', timestamp)
          )
        ),
        getDocs(
          query(
          collection(db, COLLECTION_NAMES.ACTIVE_MEETINGS),
          where('meetingTimestamp', '==', timestamp)
          )
        ),
        fetchMeetingData(timestamp),
      ]);

      const notes = notesSnapshot.docs.map((doc) => doc.data() as L10Note);
      const activeMeeting = activeMeetingsSnapshot.docs[0]?.data() as ActiveMeeting;

      // Validate meeting data was fetched successfully
      if (!meetingData) {
        throw new Error('Failed to fetch meeting data. Please try again.');
      }

      // Validate required arrays exist
      if (!Array.isArray(meetingData.rocks)) {
        throw new Error('Invalid meeting data: rocks data is missing or invalid.');
      }
      if (!Array.isArray(meetingData.issues)) {
        throw new Error('Invalid meeting data: issues data is missing or invalid.');
      }
      if (!Array.isArray(meetingData.actionItems)) {
        throw new Error('Invalid meeting data: action items data is missing or invalid.');
      }

      // Normalize section names to canonical forms
      const normalizeSectionName = (section: string): string => {
        const normalized = section.toLowerCase().trim();
        // Map variations to canonical names
        const sectionMap: Record<string, string> = {
          'presenter': 'presenter',
          'presenter_notes': 'presenter',
          'segue': 'segue',
          'segue_notes': 'segue',
          'scorecard': 'scorecard',
          'scorecard_notes': 'scorecard',
          'rock_review': 'rock_review',
          'rock_review_notes': 'rock_review',
          'customer_employee_headlines': 'customer_employee_headlines',
          'customeremployeeheadlines': 'customer_employee_headlines',
          'ids': 'ids',
          'ids_notes': 'ids',
          'ids_discussion': 'ids',
          'conclude': 'conclude',
          'conclude_notes': 'conclude',
          'conclusion': 'conclude',
        };
        return sectionMap[normalized] || normalized;
      };

      // Organize notes by section
      const sectionNotesMap: Record<string, any> = {};
      notes.forEach((note) => {
        const rawSection = note.section || note.meetingSection || 'general';
        const section = normalizeSectionName(rawSection);
        if (!sectionNotesMap[section]) {
          sectionNotesMap[section] = [];
        }
        sectionNotesMap[section].push({
          content: note.notes || '',
          departments: note.assignedDepartments || [],
          assigned_users: note.assignedUsers || [],
        });
      });

      // Call cloud function to generate summary (returns immediately, processes asynchronously)
      const summarizeMeeting = httpsCallable(functionsUsEast1, 'summarizeMeeting');

      // Transform notes into array format for summarizeMeeting
      // This keeps notes separate, which provides better context to the AI
      const sectionsForSummary = notes
        .filter((note) => note && note.notes) // Filter out empty notes
        .map((note) => {
          try {
            const rawSection = note.section || note.meetingSection || 'general';
            const section = normalizeSectionName(rawSection);
            const noteContent = note.notes || '';
            return {
              meetingSection: section,
              section: section,
              notes: noteContent,
              content: noteContent,
            };
          } catch (error) {
            console.error('Error processing note:', error, note);
            return null;
          }
        })
        .filter((note) => note !== null); // Remove any failed note processing

      // Prepare rocks data with error handling
      const rocksData = meetingData.rocks
        .filter((rock) => rock && rock.rock) // Filter out invalid rocks
        .map((rock) => ({
          rock: rock.rock || '',
          name: rock.rock || '',
          status: rock.isAchieved ? 'achieved' : (rock.status || 'unknown'),
          id: rock.id || '',
          owner: rock.ownerId || '',
        }));

      // Prepare issues data with error handling
      const issuesData = meetingData.issues
        .filter((issue) => issue && issue.name) // Filter out invalid issues
        .map((issue) => ({
          name: issue.name || '',
          issue: issue.name || '',
          description: issue.description || '',
          status: issue.status || 'open',
          id: issue.id || '',
          resolutionNotes: issue.resolutionNotes || '',
        }));

      try {
        const result = await summarizeMeeting({
          sections: sectionsForSummary,
          rocks: rocksData,
          issues: issuesData,
          meetingDate:
            activeMeeting?.meetingDate ||
            (timestamp instanceof Timestamp ? timestamp.toDate().toISOString() : String(timestamp)),
          meetingTimestamp:
            timestamp instanceof Timestamp ? timestamp.toDate().toISOString() : String(timestamp),
          generateAiRating: true,
        });
        
        const responseData = result.data as any;
        console.log('Summary generation started:', responseData);
        
        // Cloud function returns immediately with status acknowledgment
        // Real-time listener will update UI when status changes
        // No need to save to Firestore here - cloud function handles that
      } catch (error: any) {
        // Handle Firebase function errors
        const errorCode = error?.code || '';
        const errorMessage = error?.message || 'Unknown error';
        const errorDetails = error?.details || '';
        
        console.error('Error calling summarizeMeeting function:', error);
        
        // Provide user-friendly error messages for Firebase callable function errors
        if (errorCode === 'functions/unauthenticated' || errorCode === 'unauthenticated') {
          throw new Error('Authentication required. Please log in and try again.');
        } else if (errorCode === 'functions/permission-denied' || errorCode === 'permission-denied') {
          throw new Error('You do not have permission to generate summaries.');
        } else if (errorCode === 'functions/internal' || errorCode === 'functions/unknown' || errorCode === 'internal') {
          throw new Error('Server error occurred while generating summary. Please try again later.');
        } else if (errorCode === 'functions/not-found') {
          throw new Error('Summary generation service is not available. Please contact support.');
        } else if (errorCode === 'functions/deadline-exceeded') {
          throw new Error('Summary generation timed out. Please try again.');
        } else if (errorCode === 'already-exists' || errorMessage.includes('already exists') || errorMessage.includes('already in progress')) {
          throw new Error(errorMessage || 'Summary generation already in progress for this meeting');
        } else if (errorMessage) {
          // Use the error message if available, but make it user-friendly
          const friendlyMessage = errorMessage.includes('Failed to generate summary') 
            ? errorMessage 
            : `Failed to generate summary: ${errorMessage}`;
          throw new Error(friendlyMessage + (errorDetails ? ` - ${errorDetails}` : ''));
        } else {
          throw new Error('Failed to generate summary. Please try again or contact support if the issue persists.');
        }
      }
    } catch (error: any) {
      console.error('Error generating summary:', error);
      // Re-throw with improved error message if it's not already a user-friendly error
      if (error instanceof Error && error.message && !error.message.includes('Failed to') && !error.message.includes('Authentication') && !error.message.includes('permission')) {
        throw new Error(`Failed to generate summary: ${error.message}`);
      }
      throw error;
    }
  };

  // Delete summary
  const deleteSummary = async (summaryId: string) => {
    try {
      await deleteDoc(doc(db, COLLECTION_NAMES.MEETING_SUMMARIES, summaryId));
      await loadUnifiedMeetings();
    } catch (error) {
      console.error('Error deleting summary:', error);
      throw error;
    }
  };

  // Delete entire meeting (notes + summary) - for super users
  const deleteMeeting = async (meetingTimestamp: Timestamp) => {
    try {
      // Delete all notes for this meeting
      const notesQuery = query(
        collection(db, COLLECTION_NAMES.L10_NOTES),
        where('meetingTimestamp', '==', meetingTimestamp)
      );
      const notesSnapshot = await getDocs(notesQuery);
      await Promise.all(notesSnapshot.docs.map((doc) => deleteDoc(doc.ref)));

      // Delete summary if it exists
      const summaryQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_SUMMARIES),
        where('meetingTimestamp', '==', meetingTimestamp)
      );
      const summarySnapshot = await getDocs(summaryQuery);
      await Promise.all(summarySnapshot.docs.map((doc) => deleteDoc(doc.ref)));

      await loadUnifiedMeetings();
    } catch (error) {
      console.error('Error deleting meeting:', error);
      throw error;
    }
  };

  // Export to PDF using jsPDF
  const exportToPDF = async (summary: MeetingSummary) => {
    try {
      // Fetch users and departments for name resolution
      const [departmentsSnapshot] = await Promise.all([
        getDocs(query(collection(db, COLLECTION_NAMES.DEPARTMENTS), orderBy('name', 'asc'))),
      ]);

      const users = eosUsers;

      const departments = (departmentsSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Department[])
        .filter((dept) => !dept.deletedAt);

      // Helper functions to resolve IDs to names
      const getUserName = (userId: string | undefined): string => {
        if (!userId) return 'Unknown';
        const user = users.find(u => u.id === userId || u.uid === userId);
        return user?.name || user?.Name || userId;
      };

      const getDepartmentName = (deptId: string): string => {
        const dept = departments.find(d => d.id === deptId);
        return dept?.name || deptId;
      };

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let yPos = 20;

      const checkPageBreak = (spaceNeeded: number) => {
        if (yPos + spaceNeeded > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
          return true;
        }
        return false;
      };

      const addSectionHeader = (title: string, bgColor?: [number, number, number]) => {
        checkPageBreak(15);

        if (bgColor) {
          doc.setFillColor(...bgColor);
          doc.rect(margin, yPos, pageWidth - margin * 2, 7, 'F');
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(30, 30, 30);
        }

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`• ${title}`, margin + 5, yPos + 5);

        doc.setTextColor(0, 0, 0);
        yPos += 12;
      };

      const addText = (text: string, fontSize = 10, isBold = false, indent = 0) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.setTextColor(60, 60, 60);
        const lines = doc.splitTextToSize(text, contentWidth - indent);

        lines.forEach((line: string) => {
          checkPageBreak(6);
          doc.text(line, margin + indent, yPos);
          yPos += 5;
        });
      };

      const drawActivityTable = (startY: number) => {
        const tableX = margin;
        const tableWidth = 170;
        const rowHeight = 10;
        const col1Width = 130;
        const col2Width = 40;

        doc.setFillColor(70, 130, 180);
        doc.setTextColor(255, 255, 255);
        doc.rect(tableX, startY, tableWidth, rowHeight, 'F');

        doc.setDrawColor(200, 200, 200);
        doc.rect(tableX, startY, tableWidth, rowHeight, 'S');
        doc.rect(tableX, startY, col1Width, rowHeight, 'S');

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Activity', tableX + 5, startY + 7);
        doc.text('Count', tableX + col1Width + 5, startY + 7);

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        const rocksCount = Array.isArray(summary.rocksAchieved) ? summary.rocksAchieved.length : 0;
        const issuesCount = Array.isArray(summary.issuesResolved)
          ? summary.issuesResolved.length
          : summary.issues_resolved_count || 0;
        const actionItemsCount = Array.isArray(summary.actionItemsCreated) ? summary.actionItemsCreated.length : 0;

        const rows = [
          { label: 'Rocks Achieved', value: rocksCount.toString() },
          { label: 'Issues Resolved', value: issuesCount.toString() },
          { label: 'Action Items Created', value: actionItemsCount.toString() },
        ];

        rows.forEach((row, index) => {
          const y = startY + rowHeight + index * rowHeight;

          if (index % 2 === 0) {
            doc.setFillColor(245, 245, 245);
            doc.rect(tableX, y, tableWidth, rowHeight, 'F');
          }

          doc.setDrawColor(200, 200, 200);
          doc.rect(tableX, y, tableWidth, rowHeight, 'S');
          doc.rect(tableX, y, col1Width, rowHeight, 'S');

          doc.setTextColor(0, 0, 0);
          doc.text(row.label, tableX + 5, y + 7);
          doc.text(row.value, tableX + col1Width + 15, y + 7);
        });

        return startY + rowHeight + rows.length * rowHeight + 10;
      };

      const getCompanyLogoBase64 = async (): Promise<string | null> => {
        try {
          const response = await fetch('/fhl-logo.png');
          if (!response.ok) throw new Error('Logo not found');
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.warn('Failed to load logo:', error);
          return null;
        }
      };
      
      const formatMeetingDate = () => {
        const meetingDate = summary.meetingDate;
        if (!meetingDate) {
          return new Date().toLocaleDateString();
        }
        if (meetingDate instanceof Date) {
          return meetingDate.toLocaleDateString();
        }
        if (typeof meetingDate !== 'string') {
          // At this point, meetingDate is either Date (already handled) or Timestamp
          const timestamp = meetingDate as Timestamp;
          if (timestamp.toDate) {
            return timestamp.toDate().toLocaleDateString();
          }
        }
        return new Date(meetingDate as string).toLocaleDateString();
      };
      
      // Try to add logo
      try {
        const logoBase64 = await getCompanyLogoBase64();
        if (logoBase64) {
          doc.addImage(logoBase64, 'PNG', 150, 10, 45, 40);
        }
      } catch (error) {
        console.warn('Logo failed to load, continuing without logo:', error);
      }

      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('FUTURE HOME LOANS', margin, 20);

      doc.setFontSize(16);
      doc.text('L10 Meeting Summary', margin, 30);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(formatMeetingDate(), margin, 38);

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1);
      doc.line(margin, 45, pageWidth - margin, 45);

      yPos = 55;

      if (summary.avg_meeting_rating) {
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        doc.text(`Meeting Rating: ${summary.avg_meeting_rating.toFixed(1)}/10`, margin, yPos);
        yPos += 10;
      }

      // Department Communications
      const allDeptComms: Array<{ sectionName: string; notes: any[] }> = [];
      if (summary.segueNotes?.department_communications && summary.segueNotes.department_communications.length > 0) {
        allDeptComms.push({ sectionName: 'Segue', notes: summary.segueNotes.department_communications });
      }
      if (summary.scorecardNotes?.department_communications && summary.scorecardNotes.department_communications.length > 0) {
        allDeptComms.push({ sectionName: 'Scorecard', notes: summary.scorecardNotes.department_communications });
      }
      if (summary.rockReviewNotes?.department_communications && summary.rockReviewNotes.department_communications.length > 0) {
        allDeptComms.push({ sectionName: 'Rock Review', notes: summary.rockReviewNotes.department_communications });
      }
      if (summary.customerEmployeeHeadlines?.department_communications && summary.customerEmployeeHeadlines.department_communications.length > 0) {
        allDeptComms.push({ sectionName: 'Customer/Employee Headlines', notes: summary.customerEmployeeHeadlines.department_communications });
      }
      if (summary.idsNotes?.department_communications && summary.idsNotes.department_communications.length > 0) {
        allDeptComms.push({ sectionName: 'IDS Discussion', notes: summary.idsNotes.department_communications });
      }
      if (summary.concludeNotes?.department_communications && summary.concludeNotes.department_communications.length > 0) {
        allDeptComms.push({ sectionName: 'Conclusion', notes: summary.concludeNotes.department_communications });
      }

      if (allDeptComms.length > 0) {
        addSectionHeader('DEPARTMENT COMMUNICATIONS', [41, 128, 185]);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 100, 100);
        doc.text('Share • Align • Execute', 190, yPos, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        yPos += 8;

        allDeptComms.forEach(({ sectionName, notes }) => {
          if (notes && notes.length > 0) {
            checkPageBreak(10);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(40, 40, 40);
            doc.text(`From ${sectionName}:`, margin, yPos);
            yPos += 6;

            notes.forEach((comm) => {
              checkPageBreak(20);

              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(80, 80, 80);
              doc.text('•', margin + 5, yPos);

              const hasDepartment = comm.departments && comm.departments.length > 0;
              const hasOwner = comm.assigned_users && comm.assigned_users.length > 0;

              if (hasDepartment || hasOwner) {
                let xPos = margin + 10;

                if (hasDepartment) {
                  doc.setFont('helvetica', 'bold');
                  doc.text('Department:', xPos, yPos);
                  doc.setFont('helvetica', 'normal');
                  xPos += 20;
                  doc.text(comm.departments.map(getDepartmentName).join(', '), xPos, yPos);
                  xPos += 55;
                }

                if (hasOwner) {
                  doc.setFont('helvetica', 'bold');
                  doc.text('Owner:', xPos, yPos);
                  doc.setFont('helvetica', 'normal');
                  xPos += 13;
                  doc.text(comm.assigned_users.map(getUserName).join(', '), xPos, yPos);
                }

                yPos += 5;
              }

              doc.setFont('helvetica', 'normal');
              doc.setTextColor(60, 60, 60);
              const messageLines = doc.splitTextToSize(comm.content, contentWidth - 15);
              messageLines.forEach((line: string) => {
                checkPageBreak(5);
                doc.text(line, margin + 10, yPos);
                yPos += 5;
              });

              yPos += 3;
            });

            yPos += 2;
          }
        });

        yPos += 5;
      }

      // Key Takeaways & Decisions
      if (summary.concludeNotes?.general && summary.concludeNotes.general.length > 0) {
        addSectionHeader('KEY TAKEAWAYS & DECISIONS', [245, 158, 11]);

        summary.concludeNotes.general.forEach((note) => {
          checkPageBreak(8);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);
          doc.text('•', margin + 2, yPos);
          const lines = doc.splitTextToSize(note.content, contentWidth - 10);
          lines.forEach((line: string) => {
            checkPageBreak(5);
            doc.text(line, margin + 8, yPos);
            yPos += 5;
          });
          yPos += 2;
        });

        yPos += 5;
      }

      // Meeting Activity Summary
      addSectionHeader('MEETING ACTIVITY SUMMARY', [100, 100, 100]);
      yPos = drawActivityTable(yPos);

      // Section Notes
      addSectionHeader('SECTION NOTES (Reference)');

      const sections = [
        { name: 'Presenter Notes', notes: summary.presenterNotes, isArray: true },
        { name: 'Segue Notes', notes: summary.segueNotes },
        { name: 'Scorecard Notes', notes: summary.scorecardNotes },
        { name: 'Rock Review Notes', notes: summary.rockReviewNotes },
        { name: 'Customer/Employee Headlines', notes: summary.customerEmployeeHeadlines },
        { name: 'IDS Discussion Notes', notes: summary.idsNotes },
      ];

      sections.forEach(({ name, notes, isArray }) => {
        let hasContent = false;

        if (isArray) {
          hasContent = notes && Array.isArray(notes) && notes.length > 0;
        } else {
          hasContent = notes && typeof notes === 'object' && !Array.isArray(notes) && 'general' in notes && notes.general && notes.general.length > 0;
        }

        if (hasContent) {
          checkPageBreak(10);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(40, 40, 40);
          doc.text(name, margin, yPos);
          yPos += 6;

          if (isArray && Array.isArray(notes)) {
            notes.forEach((note: any) => {
              checkPageBreak(8);
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(60, 60, 60);
              const lines = doc.splitTextToSize(note.content, contentWidth - 5);
              lines.forEach((line: string) => {
                checkPageBreak(5);
                doc.text(line, margin + 5, yPos);
                yPos += 4;
              });
              yPos += 2;
            });
          } else if (notes && typeof notes === 'object' && !Array.isArray(notes) && 'general' in notes && notes.general) {
            notes.general.forEach((note: any) => {
              checkPageBreak(8);
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(60, 60, 60);
              const lines = doc.splitTextToSize(note.content, contentWidth - 5);
              lines.forEach((line: string) => {
                checkPageBreak(5);
                doc.text(line, margin + 5, yPos);
                yPos += 4;
              });
              yPos += 2;
            });
          }

          yPos += 3;
        }
      });

      // Issues Resolved
      if (Array.isArray(summary.issuesResolved) && summary.issuesResolved.length > 0) {
        checkPageBreak(15);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Issues Resolved', margin, yPos);
        yPos += 6;

        summary.issuesResolved.forEach((issue) => {
          // Handle both string and object formats for backward compatibility
          const issueName = typeof issue === 'string' ? issue : issue.name;
          const resolutionNotes = typeof issue === 'string' ? undefined : issue.resolutionNotes;
          
          checkPageBreak(12);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(34, 197, 94);
          doc.text('•', margin + 2, yPos);
          doc.setTextColor(60, 60, 60);
          const titleLines = doc.splitTextToSize(issueName, contentWidth - 10);
          titleLines.forEach((line: string) => {
            doc.text(line, margin + 8, yPos);
            yPos += 5;
          });

          if (resolutionNotes) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            const noteLines = doc.splitTextToSize(resolutionNotes, contentWidth - 10);
            noteLines.forEach((line: string) => {
              checkPageBreak(5);
              doc.text(line, margin + 8, yPos);
              yPos += 4;
            });
          }

          yPos += 3;
        });

        yPos += 3;
      }

      // Rocks Achieved
      if (Array.isArray(summary.rocksAchieved) && summary.rocksAchieved.length > 0) {
        checkPageBreak(15);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Rocks Achieved', margin, yPos);
        yPos += 6;

        summary.rocksAchieved.forEach((rock) => {
          checkPageBreak(10);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(168, 85, 247);
          doc.text('•', margin + 2, yPos);
          doc.setTextColor(60, 60, 60);
          const rockLines = doc.splitTextToSize(rock.name, contentWidth - 10);
          rockLines.forEach((line: string) => {
            doc.text(line, margin + 8, yPos);
            yPos += 5;
          });

          if (rock.owner) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`Owner: ${getUserName(rock.owner)}`, margin + 8, yPos);
            yPos += 5;
          }

          yPos += 2;
        });

        yPos += 3;
      }

      // Action Items Created
      if (Array.isArray(summary.actionItemsCreated) && summary.actionItemsCreated.length > 0) {
        checkPageBreak(15);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Action Items Created', margin, yPos);
        yPos += 6;

        summary.actionItemsCreated.forEach((actionItem) => {
          checkPageBreak(8);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);
          doc.text('•', margin + 2, yPos);
          const actionItemLines = doc.splitTextToSize(actionItem.title || actionItem.name || '', contentWidth - 10);
          actionItemLines.forEach((line: string) => {
            doc.text(line, margin + 8, yPos);
            yPos += 5;
          });
          yPos += 2;
        });
      }

      // Add page numbers and footer
      const pageCount = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

        const now = new Date();
        const timestamp = `Generated: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        doc.text(timestamp, margin, pageHeight - 10);

        doc.text('Future Home Loans EOS Platform', pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      const meetingDateStr = formatMeetingDate().replace(/\//g, '-');
      const filename = `L10_Meeting_Summary_${meetingDateStr}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to text export
      const text = `Meeting Summary\n\n${summary.summary}`;
      return text;
    }
  };

  return {
    summaries,
    unifiedMeetings,
    loading,
    generateSummary,
    deleteSummary,
    deleteMeeting,
    exportToPDF,
    loadUnifiedMeetings,
    fetchMeetingData,
  };
}
